import React, { useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { FormField } from "component/shared/form-fields";
import { OutlinedButton } from "component/shared/outlined-button";
import { normalizeWebsiteUrl, WEBSITE_URL_ERROR } from "../connect-idx-wizard/website-url";

const SAVE_ERROR = "Couldn't save your website URL. Please try again.";

interface Props {
	websiteUrl: string;
	/** Persists the new address; reject to keep the field open with an error. */
	onSave: (websiteUrl: string) => Promise<void>;
	classes: Record<string, string>;
}

/**
 * The Website URL row for an HTML site (MAX-4576, Figma `3896:26715`): the address with an Edit
 * control, which becomes an input and Done, the custom slug's pattern. Done saves at once, as Edit
 * Platform does, and checks the address the way the setup wizard does: the scripts only load IDX
 * where the site's address matches.
 */
export const WebsiteUrlRow: React.FC<Props> = ({ websiteUrl, onSave, classes }) => {
	const [editing, setEditing] = useState(false);
	const [value, setValue] = useState(websiteUrl);
	const [error, setError] = useState<string>();
	const [saving, setSaving] = useState(false);
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
	const editRef = useRef<HTMLButtonElement>(null);
	// Focus follows the switch: into the input on Edit, back to Edit on Done.
	const focusAfterSwitch = useRef<"input" | "edit">();
	useEffect(() => {
		if (focusAfterSwitch.current) {
			(focusAfterSwitch.current === "input" ? inputRef.current : editRef.current)?.focus();
			focusAfterSwitch.current = undefined;
		}
	}, [editing]);

	const open = () => {
		setValue(websiteUrl);
		setError(undefined);
		focusAfterSwitch.current = "input";
		setEditing(true);
	};

	const close = () => {
		focusAfterSwitch.current = "edit";
		setEditing(false);
	};

	const handleDone = async () => {
		const normalized = normalizeWebsiteUrl(value);
		if (!normalized) {
			setError(WEBSITE_URL_ERROR);
			return;
		}
		if (normalized === websiteUrl) {
			close();
			return;
		}
		setSaving(true);
		try {
			await onSave(normalized);
			close();
		} catch{
			setError(SAVE_ERROR);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Mui.Box className={editing ? `${classes.connectionRow} ${classes.connectionRowEditing}` : classes.connectionRow}>
			<Mui.Typography id="settings-idx-website-url-label" className={classes.rowLabel}>Website URL</Mui.Typography>
			{!editing ? (
				<Mui.Box className={classes.rowValue}>
					<Mui.Typography className={classes.rowText}>{websiteUrl}</Mui.Typography>
					<Mui.Button
						ref={editRef}
						variant="text"
						color="primary"
						startIcon={<Icons.Edit size={16} />}
						className={classes.inlineAction}
						onClick={open}
						aria-label="Edit website URL"
					>
						Edit
					</Mui.Button>
				</Mui.Box>
			) : (
				<Mui.Box className={classes.urlEditRow}>
					<Mui.Box className={classes.urlInput}>
						<FormField
							id="settings-idx-website-url"
							label="Website URL"
							hideLabel
							inputRef={inputRef}
							placeholder="https://www.yourdomain.com"
							value={value}
							onChange={(event) => {
								setValue(event.target.value);
								setError(undefined);
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									handleDone();
								}
							}}
							error={!!error}
							helperText={error}
							disabled={saving}
							spellCheck={false}
						/>
					</Mui.Box>
					<OutlinedButton className={classes.doneButton} onClick={handleDone} disabled={saving}>
						{saving ? "Saving..." : "Done"}
					</OutlinedButton>
				</Mui.Box>
			)}
		</Mui.Box>
	);
};
